// dashboard/findingReasonText.js
//
// Plain-English clinic-facing text for every vNext sentinel finding's
// reason_text_key (supabase/functions/_shared/clinicalScoring.ts's
// SentinelRule.reason_text_key / ClusterRule.reason_text_key), authored
// against the exact clinical intent of each rule as written in its
// originating migration (269, 271, 273-289).
//
// A flat lookup object, NOT routed through react-i18next's t() --
// reason_text_key values use '.' as a namespace separator
// (condition.finding_reason), which collides with i18next's default
// keySeparator nesting behavior. These are DB-driven dynamic keys, not
// static UI strings, so a plain Record<string,string> avoids that
// entirely. See buildFindingsNarrative.js for how this is composed into
// a short "why" summary.
//
// Clinic-facing register only (CLAUDE.md: concise, trend-focused,
// medically precise, does not replace clinician judgment) -- these are
// NOT owner-facing copy. Every entry states an observation, never a
// diagnosis or a recommended action (section 0 of the vNext plan).

export const FINDING_REASON_TEXT = {
  // Addison's disease
  'addisons.appetite_none_reason': 'Not eating at all.',
  'addisons.drinking_none_reason': 'Not drinking at all.',
  'addisons.energy_extreme_lethargy_reason': 'Extremely lethargic or unable to rise.',
  'addisons.energy_markedly_less_reason': 'Energy markedly reduced.',
  'addisons.med_given_no_reason': 'Addison’s medication dose missed.',
  'addisons.appetite_much_less_reason': 'Eating significantly less than usual.',
  'addisons.vomiting_yes_reason': 'Vomiting reported.',
  'addisons.diarrhea_yes_reason': 'Diarrhea reported.',
  'addisons.appetite_and_energy_cluster_reason': 'Reduced appetite together with reduced energy.',
  'addisons.appetite_and_vomiting_cluster_reason': 'Reduced appetite together with vomiting.',
  'addisons.appetite_and_diarrhea_cluster_reason': 'Reduced appetite together with diarrhea.',
  'addisons.energy_and_vomiting_cluster_reason': 'Reduced energy together with vomiting.',
  'addisons.energy_and_diarrhea_cluster_reason': 'Reduced energy together with diarrhea.',
  'addisons.vomiting_and_diarrhea_cluster_reason': 'Vomiting together with diarrhea.',

  // CHF
  'chf.appetite_none_reason': 'Not eating at all.',
  'chf.breathing_mild_reason': 'Breathing mildly more difficult than usual.',
  'chf.breathing_severe_reason': 'Struggling to breathe, unable to rest comfortably.',
  'chf.breathing_worse_reason': 'Breathing clearly more difficult than usual.',
  'chf.appetite_much_less_reason': 'Eating significantly less than usual.',
  'chf.coughing_increased_reason': 'Coughing more than usual.',
  'chf.energy_decreased_reason': 'Energy decreased.',
  'chf.appetite_and_energy_cluster_reason': 'Reduced appetite together with decreased energy.',
  'chf.coughing_and_energy_cluster_reason': 'Increased coughing together with decreased energy.',
  'chf.breathing_and_energy_cluster_reason': 'Harder breathing together with decreased energy.',

  // Chronic enteropathy
  'chronic_enteropathy.appetite_none_reason': 'Not eating at all.',
  'chronic_enteropathy.diet_changed_yes_reason': 'Diet changed since last check-in.',
  'chronic_enteropathy.stool_deteriorated_moderate_reason': 'Stool quality moderately worse (loose or soft).',
  'chronic_enteropathy.watery_diarrhea_severe_reason': 'Watery diarrhea reported.',
  'chronic_enteropathy.weight_loss_perceived_reason': 'Owner reports possible weight loss.',
  'chronic_enteropathy.appetite_much_less_reason': 'Eating significantly less than usual.',
  'chronic_enteropathy.bowel_frequency_elevated_reason': 'Bowel movement frequency elevated (5+ per day).',
  'chronic_enteropathy.energy_less_reason': 'Energy less than usual.',
  'chronic_enteropathy.vomiting_yes_reason': 'Vomiting reported.',
  'chronic_enteropathy.vomiting_and_energy_cluster_reason': 'Vomiting together with decreased energy.',
  'chronic_enteropathy.vomiting_and_appetite_cluster_reason': 'Vomiting together with reduced appetite.',
  'chronic_enteropathy.vomiting_and_bowel_frequency_cluster_reason': 'Vomiting together with elevated bowel frequency.',
  'chronic_enteropathy.energy_and_appetite_cluster_reason': 'Decreased energy together with reduced appetite.',
  'chronic_enteropathy.energy_and_bowel_frequency_cluster_reason': 'Decreased energy together with elevated bowel frequency.',
  'chronic_enteropathy.appetite_and_bowel_frequency_cluster_reason': 'Reduced appetite together with elevated bowel frequency.',

  // Chronic respiratory disease
  'chronic_respiratory.activity_little_to_none_reason': 'Tolerating little to no activity.',
  'chronic_respiratory.appetite_none_reason': 'Not eating at all.',
  'chronic_respiratory.breathing_clearly_more_difficult_reason': 'Breathing clearly more difficult than usual.',
  'chronic_respiratory.breathing_mildly_more_difficult_reason': 'Breathing mildly more difficult than usual.',
  'chronic_respiratory.breathing_struggling_reason': 'Struggling to breathe, unable to rest comfortably.',
  'chronic_respiratory.med_given_no_reason': 'Medication dose missed.',
  'chronic_respiratory.appetite_much_less_reason': 'Eating significantly less than usual.',
  'chronic_respiratory.coughing_increased_reason': 'Coughing more than usual.',
  'chronic_respiratory.appetite_and_coughing_cluster_reason': 'Reduced appetite together with increased coughing.',
  'chronic_respiratory.breathing_and_activity_cluster_reason': 'Harder breathing together with reduced activity tolerance.',

  // CKD
  'ckd.appetite_none_reason': 'Not eating at all.',
  'ckd.drinking_none_reason': 'Not drinking at all.',
  'ckd.urination_less_reason': 'Urinating a little less than usual.',
  'ckd.urination_much_less_reason': 'Urinating significantly less than usual.',
  'ckd.urination_none_reason': 'Not urinating at all.',
  'ckd.vomit_daily_reason': 'Vomiting daily or more often.',
  'ckd.vomit_several_reason': 'Vomited several times this week.',
  'ckd.weight_loss_perceived_reason': 'Owner reports possible weight loss.',
  'ckd.appetite_much_less_reason': 'Eating significantly less than usual.',
  'ckd.energy_lower_reason': 'Energy lower than usual.',
  'ckd.appetite_and_energy_cluster_reason': 'Reduced appetite together with decreased energy.',

  // Cognitive dysfunction
  'cognitive_dysfunction.disorientation_reason': 'Confusion or disorientation reported often or almost always.',
  'cognitive_dysfunction.disorientation_moderate_reason': 'Confusion or disorientation reported sometimes.',
  'cognitive_dysfunction.qol_poor_reason': 'Overall quality of life reported as poor.',
  'cognitive_dysfunction.qol_poor_moderate_reason': 'Overall quality of life reported as reduced.',
  'cognitive_dysfunction.sleep_pacing_reason': 'Nighttime pacing or sleep disruption reported often or almost always.',
  'cognitive_dysfunction.sleep_pacing_moderate_reason': 'Nighttime pacing or sleep disruption reported sometimes.',
  'cognitive_dysfunction.interaction_withdrawn_reason': 'Withdrawn or avoiding contact with family.',
  'cognitive_dysfunction.house_accidents_elevated_reason': 'House-soiling accidents elevated (3+ per period).',
  'cognitive_dysfunction.withdrawn_and_house_accidents_cluster_reason': 'Withdrawal from family together with elevated house-soiling accidents.',

  // Cushing's disease
  'cushings.appetite_none_reason': 'Not eating at all.',
  'cushings.not_drinking_reason': 'Not drinking at all.',
  'cushings.not_urinating_reason': 'Not urinating at all.',
  'cushings.appetite_much_less_reason': 'Eating significantly less than usual.',
  'cushings.activity_less_reason': 'Activity level less than usual.',
  'cushings.pant_more_reason': 'Panting more than usual.',
  'cushings.appetite_and_activity_cluster_reason': 'Reduced appetite together with decreased activity.',
  'cushings.appetite_and_panting_cluster_reason': 'Reduced appetite together with increased panting.',
  'cushings.activity_and_panting_cluster_reason': 'Decreased activity together with increased panting.',

  // Diabetes mellitus
  'diabetes.appetite_none_reason': 'Not eating at all.',
  'diabetes.hypoglycemia_emergency_reason': 'Marked disorientation, collapse, or seizure activity reported.',
  'diabetes.hypoglycemia_mild_reason': 'Mild unusual sleepiness reported.',
  'diabetes.hypoglycemia_urgent_reason': 'Weakness, wobbliness, or trembling reported.',
  'diabetes.insulin_missed_multiple_reason': 'Multiple insulin doses missed.',
  'diabetes.insulin_missed_one_reason': 'One insulin dose missed.',
  'diabetes.not_drinking_reason': 'Not drinking at all.',
  'diabetes.not_urinating_reason': 'Not urinating at all.',
  'diabetes.weight_loss_perceived_reason': 'Owner reports possible weight loss.',
  'diabetes.appetite_much_less_reason': 'Eating significantly less than usual.',
  'diabetes.vomit_yes_reason': 'Vomiting reported.',
  'diabetes.appetite_and_vomiting_cluster_reason': 'Reduced appetite together with vomiting.',
  'diabetes.energy_decreased_reason': 'Energy decreased.',
  'diabetes.appetite_and_energy_cluster_reason': 'Reduced appetite together with decreased energy.',
  'diabetes.vomiting_and_energy_cluster_reason': 'Vomiting together with decreased energy.',

  // Epilepsy
  'epilepsy.breakthrough_seizures_frequent_reason': '3 or more seizures reported this period.',
  'epilepsy.breakthrough_seizures_mild_reason': '1-2 seizures reported this period.',
  'epilepsy.cluster_yes_reason': 'Cluster seizures reported (multiple within 24 hours).',
  'epilepsy.duration_over_5min_reason': 'A seizure lasted longer than 5 minutes.',
  'epilepsy.interictal_significantly_abnormal_reason': 'Behavior between seizures reported as significantly abnormal.',
  'epilepsy.med_missed_multiple_reason': 'Multiple anti-seizure medication doses missed.',
  'epilepsy.med_missed_one_reason': 'One anti-seizure medication dose missed.',
  'epilepsy.recovery_over_1hr_reason': 'Recovery after the most recent seizure took over an hour.',

  // Chronic hepatopathy
  'hepatopathy.appetite_none_reason': 'Not eating at all.',
  'hepatopathy.drinking_none_reason': 'Not drinking at all.',
  'hepatopathy.med_given_no_reason': 'Medication dose missed.',
  'hepatopathy.appetite_much_less_reason': 'Eating significantly less than usual.',
  'hepatopathy.energy_less_reason': 'Energy less than usual.',
  'hepatopathy.vomiting_yes_reason': 'Vomiting reported.',
  'hepatopathy.appetite_and_energy_cluster_reason': 'Reduced appetite together with decreased energy.',
  'hepatopathy.appetite_and_vomiting_cluster_reason': 'Reduced appetite together with vomiting.',
  'hepatopathy.energy_and_vomiting_cluster_reason': 'Decreased energy together with vomiting.',
  'hepatopathy.jaundice_reason': 'Yellowing of skin, gums, or eyes reported.',
  'hepatopathy.ascites_reason': 'Abdominal swelling or bloating reported.',
  'hepatopathy.neuro_signs_reason': 'Confusion, disorientation, or unresponsiveness reported.',

  // Hyperthyroidism
  'hyperthyroidism.appetite_none_reason': 'Not eating at all.',
  'hyperthyroidism.drinking_none_reason': 'Not drinking at all.',
  'hyperthyroidism.med_given_no_reason': 'Medication dose missed.',
  'hyperthyroidism.weight_loss_perceived_reason': 'Owner reports possible weight loss.',
  'hyperthyroidism.appetite_increased_reason': 'Eating more than usual.',
  'hyperthyroidism.appetite_and_weight_loss_cluster_reason': 'Increased appetite together with perceived weight loss.',
  'hyperthyroidism.appetite_much_less_reason': 'Eating significantly less than usual.',
  'hyperthyroidism.vomit_repeated_reason': 'Repeated vomiting reported.',
  'hyperthyroidism.vomit_one_isolated_reason': 'One isolated vomiting episode.',
  'hyperthyroidism.appetite_and_vomiting_cluster_reason': 'Reduced appetite together with a vomiting episode.',

  // Immune-mediated disease
  'immune.active_bleeding_reason': 'Active bleeding reported (nosebleed, gum bleeding, or similar).',
  'immune.appetite_none_reason': 'Not eating at all.',
  'immune.black_bloody_stool_reason': 'Black or bloody stool reported.',
  'immune.breathing_clearly_faster_reason': 'Breathing clearly faster or harder than usual.',
  'immune.breathing_struggling_reason': 'Struggling to breathe, unable to rest comfortably.',
  'immune.bruising_mild_reason': 'Mild bruising or small red spots reported.',
  'immune.collapse_reason': 'Collapse reported.',
  'immune.dark_urine_reason': 'Dark urine reported.',
  'immune.energy_much_lower_reason': 'Energy much lower than usual.',
  'immune.energy_very_weak_reason': 'Very weak or having trouble standing.',
  'immune.gi_cannot_retain_reason': 'Unable to keep down food or water.',
  'immune.gi_repeated_reason': 'Repeated vomiting or diarrhea reported.',
  'immune.med_multiple_missed_reason': 'Multiple medication doses missed.',
  'immune.med_stopped_reason': 'Medication stopped or unable to give.',
  'immune.mucosa_blue_gray_white_reason': 'Gums reported blue, gray, or very white.',
  'immune.not_urinating_normally_reason': 'Not urinating normally.',
  'immune.pale_gums_reason': 'Gums reported paler than usual.',
  'immune.yellow_discoloration_reason': 'Yellow discoloration reported.',
  'immune.appetite_much_less_reason': 'Eating significantly less than usual.',
  'immune.hemolysis_cluster_reason': 'Dark urine together with yellow discoloration.',
  'immune.anemia_cluster_reason': 'Reduced energy together with paler gums.',
  'immune.cardiorespiratory_cluster_reason': 'Harder breathing together with reduced energy or weakness.',
  'immune.treatment_complication_weakness_cluster_reason': 'Repeated vomiting or diarrhea together with reduced energy or weakness.',
  'immune.bleeding_with_weakness_cluster_reason': 'Bruising together with reduced energy or weakness.',
  'immune.treatment_interruption_cluster_reason': 'An interrupted medication together with reduced energy or weakness.',
  'immune.treatment_complication_appetite_cluster_reason': 'Repeated vomiting or diarrhea together with reduced appetite.',
  'immune.broader_deterioration_cluster_reason': 'Reduced energy, reduced appetite, and a change in gum color together.',

  // Oncology support
  'oncology.appetite_none_reason': 'Not eating at all.',
  'oncology.comfort_poor_reason': 'Comfort level reported as poor.',
  'oncology.comfort_very_poor_reason': 'Comfort level reported as very poor.',
  'oncology.med_given_no_reason': 'Medication dose missed.',
  'oncology.qol_poor_reason': 'Overall quality of life reported as poor.',
  'oncology.qol_very_poor_reason': 'Overall quality of life reported as very poor.',
  'oncology.vomit_cannot_retain_reason': 'Unable to keep down food or water.',
  'oncology.vomit_one_isolated_reason': 'One isolated vomiting episode.',
  'oncology.vomit_repeated_reason': 'Repeated vomiting reported.',
  'oncology.weight_loss_perceived_reason': 'Owner reports possible weight loss.',
  'oncology.appetite_much_less_reason': 'Eating significantly less than usual.',
  'oncology.energy_less_reason': 'Energy less than usual.',
  'oncology.appetite_and_energy_cluster_reason': 'Reduced appetite together with reduced energy.',

  // Osteoarthritis
  'osteoarthritis.cannot_rise_reason': 'Unable to rise without help.',
  'osteoarthritis.cannot_walk_reason': 'Unable to walk.',
  'osteoarthritis.pain_constant_reason': 'Signs of pain observed constantly.',
  'osteoarthritis.refuses_activity_reason': 'Refusing all activity.',
  'osteoarthritis.comfort_poor_reason': 'Overall comfort reported as poor.',
  'osteoarthritis.comfort_reduced_reason': 'Overall comfort reduced.',
  'osteoarthritis.activity_reduced_reason': 'Willingness to be active reduced.',
  'osteoarthritis.rising_difficulty_reason': 'Difficulty rising more frequent.',
  'osteoarthritis.walking_difficulty_reason': 'Difficulty walking more frequent.',
  'osteoarthritis.pain_frequent_reason': 'Signs of pain observed frequently.',

  // Pruritus
  'pruritus.itch_moderate_severe_reason': 'Itching reported as moderate to severe.',
  'pruritus.itch_severe_reason': 'Itching reported as severe.',
  'pruritus.skin_worsening_reason': 'Skin or coat appearance worse than when well-controlled.',
  'pruritus.sleep_disruption_significant_reason': 'Scratching disturbing sleep often or every night.',

  // QOL general
  'qol_general.does_not_move_for_needs_reason': 'Not moving around even when appearing to need something.',
  'qol_general.does_not_move_for_needs_some_reason': 'Some reluctance to move around when appearing to need something.',
  'qol_general.more_bad_days_reason': 'More bad days than good days reported.',
  'qol_general.more_bad_days_some_reason': 'Some increase in bad days reported.',
  'qol_general.not_eating_reason': 'Not eating.',
  'qol_general.not_urinating_reason': 'Not urinating.',
  'qol_general.overall_qol_poor_reason': 'Overall quality of life reported as very poor.',
  'qol_general.overall_qol_very_poor_reason': 'Overall quality of life rated very poor.',
  'qol_general.pain_reason': 'Pain reported.',
  'qol_general.pain_some_reason': 'Some signs of pain reported.',

  // SRR (CHF companion, raw breaths/min)
  'srr.elevated': 'Resting respiratory rate elevated (30-39 breaths/min).',
  'srr.critical': 'Resting respiratory rate critical (40-49 breaths/min).',
  'srr.immediate_review': 'Resting respiratory rate critical (50+ breaths/min).',

  // Urinary tract disease
  'utd.blood_and_abnormal_location_cluster_reason': 'Blood in urine together with urinating outside the normal location.',
  'utd.blood_in_urine_reason': 'Blood in urine reported.',
  'utd.not_urinating_normally_reason': 'Not urinating normally.',
  'utd.outside_normal_location_reason': 'Urinating outside the normal location.',
  'utd.straining_reason': 'Straining to urinate reported.',
  'utd.appetite_energy_worse_reason': 'Appetite and energy worse than usual.',
  'utd.appetite_energy_and_blood_cluster_reason': 'Worse appetite and energy together with blood in urine.',
  'utd.appetite_energy_and_location_cluster_reason': 'Worse appetite and energy together with urinating outside the normal location.',
};

export function findingReasonText(reasonTextKey) {
  return FINDING_REASON_TEXT[reasonTextKey] ?? null;
}
